import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/crmApi";
import { PageHeader, StatusBadge, ScoreBadge, Loader, Empty } from "@/crm/ui";
import { ClientChip } from "@/crm/components/ClientControls";
import CallCampaignForm from "@/crm/components/CallCampaignForm";
import { useAuth } from "@/context/CrmAuth";
import AddressAutocomplete from "@/crm/components/AddressAutocomplete";
import useDebugMode from "@/crm/components/debug/useDebugMode";
import { toast } from "sonner";
import { ageFromDate, formatDateCreation, isRecent } from "@/crm/anciennete";
import CompanyFilters, {
  STATUSES, EMPTY_FILTERS, hydrateFilters, filtersToParams, filtersToSearchParams,
} from "@/crm/components/CompanyFilters";
import {
  Building2, Plus, Phone, Mail, Globe, Eye, X, Sparkles, Trash2, AlertTriangle, Zap,
  ArrowUp, ArrowDown, ArrowUpDown, PhoneOutgoing,
} from "lucide-react";
import Modal from "@/crm/components/Modal";
import QuickAddCompany from "@/crm/components/QuickAddCompany";
import { VERDICT_STYLE, verdictLabel, motifLabel, recheckDue } from "@/crm/qualification";

/** Pastille de qualification sous le statut : verdict, premier motif, échéance. */
function QualificationChip({ q }) {
  if (!q?.verdict) return null;
  const motif = (q.motifs || [])[0];
  return (
    <span data-testid="qualification-chip" title={(q.motifs || []).map(motifLabel).join(", ") || verdictLabel(q.verdict)}
      className={`mt-1 inline-flex max-w-[190px] items-center gap-1 truncate rounded-md border px-1.5 py-0.5 text-[10.5px] font-medium ${VERDICT_STYLE[q.verdict] || ""}`}>
      {verdictLabel(q.verdict)}{motif && <> · {motifLabel(motif)}</>}{recheckDue(q) && " ⏰"}
    </span>
  );
}

export default function Companies() {
  const { user } = useAuth();
  // « Purger la base » : admin ET Mode Debug ouvert. Le serveur exige déjà les
  // deux (409 sinon) ; afficher le bouton en permanence promettait une action
  // que la page ne pouvait pas tenir — l'admin le découvrait après avoir ouvert
  // la modale. Même règle que le bouton « Purger » de la fiche entreprise.
  const { debugActive } = useDebugMode({ enabled: user?.role === "admin" });
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [page, setPage] = useState(() => Number(sp.get("page")) || 1);
  const [selected, setSelected] = useState([]);
  // « Toutes les pages » : la sélection est alors définie par les filtres, pas
  // par des cases cochées. Retombe dès qu'une case bouge ou que la sélection
  // se vide (changement de filtre, de page, action terminée).
  const [allFiltered, setAllFiltered] = useState(false);
  const [commercials, setCommercials] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [showCallCampaign, setShowCallCampaign] = useState(false);
  const [showPurge, setShowPurge] = useState(false);
  // L'état des filtres vient de l'URL : une liste filtrée se partage et survit
  // à un rechargement (c'est aussi comme ça qu'arrive `campaign_id`).
  const [filters, setFilters] = useState(() => hydrateFilters(sp));
  const [search, setSearch] = useState(() => sp.get("search") || "");
  // La recherche part au clavier ; on n'interroge l'API qu'à la pause de frappe.
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Une recherche peut arriver de l'extérieur (palette ⌘K de l'en-tête) alors
  // que la page est déjà montée : l'état initial ne se rejoue pas, il faut
  // adopter la valeur de l'URL. On ignore les écritures faites par la page
  // elle-même — sinon une frappe en cours serait réécrite par la valeur
  // précédente, publiée dans l'URL avec un temps de retard (anti-rebond).
  const urlSearch = sp.get("search") || "";
  const writtenSearch = useRef(urlSearch);
  useEffect(() => {
    if (urlSearch === writtenSearch.current) return;
    writtenSearch.current = urlSearch;
    setPage(1);
    setSelected([]);
    setSearch(urlSearch);
    setDebouncedSearch(urlSearch);
    // Les filtres suivent la même URL : sans ça, une recherche lancée depuis
    // la palette resterait bridée par les filtres de l'écran précédent, et
    // l'effet d'écriture les réinjecterait aussitôt dans l'URL.
    setFilters(hydrateFilters(sp));
  }, [urlSearch, sp]);

  const queryParams = useMemo(
    () => filtersToParams(filters, debouncedSearch),
    [filters, debouncedSearch]
  );

  // Toute modification de filtre ramène en page 1 : rester en page 7 d'un
  // résultat qui n'en a plus que 2 afficherait une liste vide.
  const updateFilters = (next) => { setPage(1); setSelected([]); setFilters(next); };
  // Tri par en-tête de colonne. Même état que le menu « Tri : » (filters.sort,
  // envoyé tel quel à l'API) : cliquer une colonne, c'est choisir un tri, pas
  // un second mécanisme. Un clic trie, un second inverse le sens.
  const sortKey = filters.sort.replace(/^-/, "");
  const sortDesc = filters.sort.startsWith("-");
  const sortBy = (field, defaultDesc = false) => {
    const next = sortKey === field ? (sortDesc ? field : `-${field}`) : (defaultDesc ? `-${field}` : field);
    updateFilters({ ...filters, sort: next });
  };
  const sortProps = { sortKey, sortDesc, onSort: sortBy };
  const updateSearch = (v) => { setPage(1); setSearch(v); };
  const resetFilters = () => { setPage(1); setSelected([]); setSearch(""); setFilters({ ...EMPTY_FILTERS }); };

  // Écriture de l'état dans l'URL. On compare la query string avant d'écrire :
  // `setSp` provoque une navigation, donc écrire une valeur identique à chaque
  // rendu bouclerait.
  useEffect(() => {
    const next = new URLSearchParams(filtersToSearchParams(filters, debouncedSearch));
    if (page > 1) next.set("page", String(page));
    if (next.toString() !== window.location.search.replace(/^\?/, "")) {
      writtenSearch.current = debouncedSearch;
      setSp(next, { replace: true });
    }
  }, [filters, debouncedSearch, page, setSp]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/companies", { params: { ...queryParams, sort: filters.sort, page, limit: 25 } });
      setData(data);
    } catch (e) { toast.error("Erreur de chargement"); }
    setLoading(false);
  }, [page, queryParams, filters.sort]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/users/commercials").then(({ data }) => setCommercials(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/campaigns/options").then(({ data }) => setCampaigns(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/settings/naf-catalog").then(({ data }) => setSectors(data)).catch(() => {}); }, []);

  const toggle = (id) => { setAllFiltered(false); setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]); };
  const toggleAll = () => { setAllFiltered(false); setSelected(selected.length === data.items.length ? [] : data.items.map((c) => c.id)); };
  useEffect(() => { if (selected.length === 0) setAllFiltered(false); }, [selected.length]);
  const pageFullySelected = data.items.length > 0 && selected.length === data.items.length;
  const selectionCount = allFiltered ? data.total : selected.length;

  const bulk = async (action, value) => {
    // Toutes les pages : le serveur résout les mêmes filtres que la liste.
    const target = allFiltered ? { company_ids: [], filters: queryParams } : { company_ids: selected };
    try {
      const { data: res } = await api.post("/companies/bulk", { ...target, action, value });
      if (action === "enrich") toast.success("Enrichissement lancé en arrière-plan");
      else toast.success(`${res.count || selectionCount} entreprise(s) mises à jour`);
      setSelected([]);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  // L'export reprend exactement les filtres affichés (même construction de
  // requête côté serveur), à une exception près : les entreprises en opposition
  // ne sortent jamais d'un export. On retire donc ce filtre et on le dit, sinon
  // « Oppositions uniquement » renverrait un fichier vide sans explication.
  const exportCsv = async () => {
    const params = { ...queryParams };
    if (params.excluded) {
      delete params.excluded;
      toast.info("Les entreprises en opposition ne sont jamais exportées.");
    }
    try {
      const { data } = await api.get("/export/companies", { params, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "prospects.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Export téléchargé");
    } catch { toast.error("Échec de l'export"); }
  };

  const isManager = user?.role !== "commercial";
  const isAdmin = user?.role === "admin";

  return (
    <div>
      <PageHeader
        title="Entreprises"
        subtitle={`${data.total} entreprise(s)`}
        actions={
          <div className="flex gap-2">
            {user?.role === "admin" && debugActive && (
              <button data-testid="purge-btn" onClick={() => setShowPurge(true)}
                title="Purger toute la base (Mode Debug actif)"
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-rose-300 text-sm text-rose-600 hover:bg-rose-50">
                <Trash2 size={16} /> Purger la base
              </button>
            )}
            {/* Corbeille des fiches : accessible d'ici et depuis une fiche, sans
                entrée de menu dédiée. Réservée à l'administrateur de plateforme
                (le serveur refuse les autres), voir pages/Trash.jsx. */}
            {user?.platform_admin && (
              <button data-testid="trash-btn" onClick={() => navigate("/corbeille")}
                title="Fiches supprimées, restaurables pendant 30 jours"
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50">
                <Trash2 size={16} /> Corbeille
              </button>
            )}
            {/* Ajout rapide en premier : chercher l'entreprise au registre
                public donne une fiche complète, la saisie manuelle ne sert plus
                qu'aux cas qu'il ne connaît pas. */}
            <button data-testid="quick-add-btn" onClick={() => setShowQuickAdd(true)}
              title="Chercher une entreprise sur le registre public et l'ajouter"
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 text-sm hover:bg-indigo-100">
              <Zap size={16} /> Ajout rapide
            </button>
            <button data-testid="new-company-btn" onClick={() => setShowNew(true)} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700">
              <Plus size={16} /> Nouvelle entreprise
            </button>
          </div>
        }
      />

      <CompanyFilters
        filters={filters}
        onChange={updateFilters}
        search={search}
        onSearch={updateSearch}
        commercials={commercials}
        campaigns={campaigns}
        sectors={sectors}
        isManager={isManager}
        isAdmin={isAdmin}
        onExport={exportCsv}
        onReset={resetFilters}
      />

      {selected.length > 0 && (
        <div className="bg-indigo-600 text-white rounded-xl p-3 mb-3 flex flex-wrap items-center gap-3" data-testid="bulk-bar">
          <span className="font-medium text-sm" data-testid="bulk-count">
            {selectionCount} sélectionnée(s){allFiltered && " · toutes les pages"}
          </span>
          {/* La page entière est cochée mais la liste continue : proposer
              d'étendre aux pages suivantes, sans jamais le faire d'office. */}
          {!allFiltered && pageFullySelected && data.total > data.items.length && (
            <button data-testid="select-all-filtered" onClick={() => setAllFiltered(true)}
              className="text-sm underline underline-offset-2 text-white/90 hover:text-white">
              Sélectionner les {data.total} entreprises filtrées
            </button>
          )}
          {allFiltered && (
            <button data-testid="select-page-only" onClick={() => setAllFiltered(false)}
              className="text-sm underline underline-offset-2 text-white/90 hover:text-white">
              Seulement cette page
            </button>
          )}
          {isManager && (
            <select data-testid="bulk-assign" onChange={(e) => e.target.value && bulk("assign", e.target.value)} defaultValue=""
              className="text-sm rounded-lg px-2 py-1.5 text-slate-800">
              <option value="" disabled>Attribuer à…</option>
              {commercials.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <select data-testid="bulk-status" onChange={(e) => e.target.value && bulk("status", e.target.value)} defaultValue=""
            className="text-sm rounded-lg px-2 py-1.5 text-slate-800">
            <option value="" disabled>Changer statut…</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button data-testid="bulk-enrich" onClick={() => bulk("enrich", ["maps", "website", "email"])} className="flex items-center gap-1.5 text-sm bg-white/20 hover:bg-white/30 rounded-lg px-3 py-1.5">
            <Sparkles size={15} /> Enrichir
          </button>
          {/* Campagne d'appel à partir de la sélection (admin) : même
              formulaire que depuis le menu, la cible étant les fiches cochées. */}
          {isAdmin && (
            <button data-testid="bulk-call-campaign" onClick={() => setShowCallCampaign(true)}
              className="flex items-center gap-1.5 text-sm bg-white/20 hover:bg-white/30 rounded-lg px-3 py-1.5">
              <PhoneOutgoing size={15} /> Campagne d'appel
            </button>
          )}
          <button onClick={() => setSelected([])} className="ml-auto text-white/80 hover:text-white"><X size={18} /></button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? <Loader /> : data.items.length === 0 ? <Empty label="Aucune entreprise" icon={Building2} /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="p-3 w-8"><input type="checkbox" data-testid="select-all" checked={selected.length === data.items.length && data.items.length > 0} onChange={toggleAll} /></th>
                  <SortTh field="nom" label="Entreprise" {...sortProps} />
                  <SortTh field="libelle_naf" label="Activité" {...sortProps} />
                  <SortTh field="ville" label="Ville" {...sortProps} />
                  <th className="p-3 text-left font-semibold">Contact</th>
                  <SortTh field="lead_score" label="Score" defaultDesc {...sortProps} />
                  <SortTh field="status" label="Statut" {...sortProps} />
                  {isManager && <SortTh field="commercial_id" label="Commercial" {...sortProps} />}
                  <th className="p-3 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.items.map((c) => (
                  <tr key={c.id} data-testid={`company-row-${c.id}`} className="hover:bg-slate-50">
                    <td className="p-3"><input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} data-testid={`select-${c.id}`} /></td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-800">{c.nom}</span>
                        {/* Hors prospection : la pastille évite d'ouvrir la
                            fiche pour comprendre pourquoi on ne l'appelle pas. */}
                        <ClientChip company={c} />
                        {/* Repère de prospection : une entreprise de moins de
                            6 mois n'a en général pas encore de prestataire. */}
                        {isRecent(ageFromDate(c.date_creation)) && (
                          <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold"
                            title={`Créée le ${formatDateCreation(c.date_creation)}`}>
                            RÉCENTE
                          </span>
                        )}
                      </div>
                      {c.siret && <div className="text-xs text-slate-400 font-mono">{c.siret}</div>}
                    </td>
                    {/* Activité = libellé du code NAF (« Restauration de type
                        rapide »), plus parlant qu'une division ; le secteur
                        (« 56 - Restauration ») reste en info-bulle, et prend le
                        relais si la fiche n'a pas de libellé. */}
                    <td className="max-w-[260px] p-3 text-slate-600" title={[c.secteur, c.naf].filter(Boolean).join(" · ")}>
                      <span className="line-clamp-2">{c.libelle_naf || c.secteur || <span className="text-slate-300">—</span>}</span>
                    </td>
                    <td className="p-3 text-slate-600">{c.ville} <span className="text-slate-400 font-mono text-xs">{c.code_postal}</span></td>
                    <td className="p-3">
                      <div className="flex gap-1.5 text-slate-400">
                        <Phone size={14} className={c.telephone ? "text-emerald-500" : ""} />
                        <Mail size={14} className={c.email ? "text-emerald-500" : ""} />
                        <Globe size={14} className={c.site_web ? "text-emerald-500" : ""} />
                      </div>
                    </td>
                    <td className="p-3"><ScoreBadge score={c.lead_score} category={c.score_category} /></td>
                    <td className="p-3">
                      <StatusBadge status={c.status} />
                      <QualificationChip q={c.qualification} />
                    </td>
                    {isManager && <td className="p-3 text-slate-600 text-xs">{c.commercial_name || "—"}</td>}
                    <td className="p-3 text-right">
                      <button data-testid={`view-${c.id}`} onClick={() => navigate(`/entreprises/${c.id}`)} className="p-1.5 rounded-lg hover:bg-indigo-50 text-indigo-600">
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data.pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm disabled:opacity-40">Précédent</button>
          <span className="text-sm text-slate-500">Page {page} / {data.pages}</span>
          <button disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm disabled:opacity-40">Suivant</button>
        </div>
      )}

      {showCallCampaign && (
        <CallCampaignForm companyIds={allFiltered ? undefined : selected} initialFilters={allFiltered ? queryParams : undefined}
          onClose={() => setShowCallCampaign(false)}
          onSaved={(c) => { setShowCallCampaign(false); setSelected([]); navigate(`/campagnes/appel/${c.id}`); }} />
      )}
      {showQuickAdd && <QuickAddCompany onClose={() => setShowQuickAdd(false)} onAdded={load} />}
      {showNew && <NewCompanyModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {showPurge && <PurgeModal total={data.total} onClose={() => setShowPurge(false)} onDone={() => { setShowPurge(false); setSelected([]); setPage(1); load(); }} />}
    </div>
  );
}

/** En-tête de colonne triable : un clic trie, un second inverse le sens. */
function SortTh({ field, label, defaultDesc = false, sortKey, sortDesc, onSort }) {
  const active = sortKey === field;
  return (
    <th className="p-3 text-left font-semibold" aria-sort={active ? (sortDesc ? "descending" : "ascending") : undefined}>
      <button type="button" data-testid={`sort-${field}`} onClick={() => onSort(field, defaultDesc)}
        className={`inline-flex items-center gap-1 uppercase hover:text-slate-800 ${active ? "text-slate-900" : ""}`}>
        {label}
        {active ? (sortDesc ? <ArrowDown size={12} /> : <ArrowUp size={12} />) : <ArrowUpDown size={12} className="text-slate-300" />}
      </button>
    </th>
  );
}

function PurgeModal({ total, onClose, onDone }) {
  const [confirm, setConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [debugActive, setDebugActive] = useState(null);
  const [loading, setLoading] = useState(false);
  // Action sensible : le serveur exige un Mode Debug actif ET le code du Mode
  // Debug ; on l'annonce avant la saisie plutôt qu'après un 409.
  useEffect(() => {
    let alive = true;
    api.get("/admin/debug/status").then(({ data }) => { if (alive) setDebugActive(Boolean(data.active)); })
      .catch(() => { if (alive) setDebugActive(false); });
    return () => { alive = false; };
  }, []);
  const ready = confirm.trim().toUpperCase() === "SUPPRIMER" && password.length > 0 && debugActive;
  const purge = async () => {
    if (!ready || loading) return;
    setLoading(true);
    try {
      const { data } = await api.post("/companies/purge", { password, confirm: true });
      toast.success(`Base purgée : ${data.deleted.companies} entreprise(s) supprimée(s)`);
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  return (
    <Modal onClose={onClose} testid="purge-modal" label="Purger toute la base">
      <div className="flex items-center gap-3 mb-3 text-rose-600">
        <div className="h-11 w-11 rounded-full bg-rose-50 flex items-center justify-center"><AlertTriangle size={22} /></div>
        <h3 className="font-heading font-bold text-lg text-slate-900">Purger toute la base</h3>
      </div>
      <p className="text-sm text-slate-600 mb-4">
        Cette action supprime <b>définitivement</b> les <b>{total}</b> entreprise(s) ainsi que leurs contacts, activités, tâches, emails et historiques. Les campagnes sont conservées mais réinitialisées. <b>Irréversible.</b>
      </p>
      {debugActive === false && (
        <div data-testid="purge-needs-debug" className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Action sensible : le Mode Debug doit être actif. Activez-le depuis <b>Administration › Paramètres › Mode Debug</b>.
        </div>
      )}
      <label className="text-xs text-slate-500">Tapez <b>SUPPRIMER</b> pour confirmer</label>
      <input data-testid="purge-confirm-input" value={confirm} autoFocus onChange={(e) => setConfirm(e.target.value)} placeholder="SUPPRIMER"
        className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:ring-2 focus:ring-rose-500" />
      <label className="text-xs text-slate-500 block mt-3">Code du Mode Debug</label>
      <input data-testid="purge-password-input" type="password" value={password} autoComplete="off" disabled={debugActive === false}
        onChange={(e) => setPassword(e.target.value)} placeholder="••••"
        onKeyDown={(e) => { if (e.key === "Enter") purge(); }}
        className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:ring-2 focus:ring-rose-500 disabled:bg-slate-50" />
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-sm">Annuler</button>
        <button data-testid="purge-confirm-btn" onClick={purge} disabled={!ready || loading}
          className="px-4 py-2 rounded-lg bg-rose-600 text-white text-sm hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed">
          {loading ? "Suppression…" : "Tout supprimer"}
        </button>
      </div>
    </Modal>
  );
}

function NewCompanyModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ nom: "", siret: "", telephone: "", email: "", site_web: "", adresse: "", code_postal: "", ville: "", departement: "", naf: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.nom) return toast.error("Le nom est requis");
    setSaving(true);
    try {
      await api.post("/companies", form);
      toast.success("Entreprise créée");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };
  return (
    <Modal onClose={onClose} size="lg" testid="new-company-modal" label="Nouvelle entreprise">
      <h3 className="font-heading font-bold text-lg mb-4">Nouvelle entreprise</h3>
      {/* Une colonne sous 640 px : à deux, les champs (SIRET, code postal…)
          descendent sous la largeur lisible sur un téléphone. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[["nom", "Nom *"], ["siret", "SIRET"], ["telephone", "Téléphone"], ["email", "Email"], ["site_web", "Site web"], ["adresse", "Adresse"], ["code_postal", "Code postal"], ["ville", "Ville"], ["departement", "Département"], ["naf", "Code NAF"]].map(([k, label]) => (
          <div key={k} className={k === "adresse" ? "sm:col-span-2" : ""}>
            <label className="text-xs text-slate-500">{label}</label>
            {k === "adresse" ? (
              // Une adresse choisie dans la liste renseigne aussi code postal,
              // ville et département — ce sont eux qui pilotent la
              // sectorisation, ils ne doivent pas dépendre d'une frappe.
              <AddressAutocomplete
                testId="new-adresse" className="mt-1" value={form.adresse}
                onChange={(v) => setForm((f) => ({ ...f, adresse: v }))}
                onSelect={(a) => setForm((f) => ({
                  ...f, adresse: a.adresse,
                  code_postal: a.code_postal || f.code_postal,
                  ville: a.ville || f.ville,
                  departement: a.departement || f.departement,
                }))}
                inputClassName="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:ring-2 focus:ring-indigo-500" />
            ) : (
              <input data-testid={`new-${k}`} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:ring-2 focus:ring-indigo-500" />
            )}
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-sm">Annuler</button>
        <button data-testid="save-company" onClick={save} disabled={saving} className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700">{saving ? "…" : "Créer"}</button>
      </div>
    </Modal>
  );
}
